import axios from 'axios'
import { ACCESS_TOKEN_KEY } from './webSession'

/**
 * Every Laravel API route needs a JWT (pentest FI-1), including the export
 * downloads that used to be opened with window.location.
 */

export function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  } catch (e) {
    return null
  }
}

/**
 * Headers for a Laravel call: `extra` plus the current access token.
 * The token is read at call time, so a token refreshed by the web session
 * keeper is picked up without reloading the page.
 */
export function authHeaders(extra = {}) {
  const token = getAccessToken()
  return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra }
}

/**
 * Laravel's download/{pathFile} takes a path with dots for slashes:
 * "storage.app.export.report_1.xlsx" is "storage/app/export/report_1.xlsx".
 */
export function fileNameOfDownloadPath(path) {
  const value = String(path || '')
  const match = value.match(/^(.*)\.([^.\s]{3,4})$/)
  if (!match) return value.split('.').pop() || 'download'
  const name = match[1].split('.').pop()
  return name ? `${name}.${match[2]}` : `download.${match[2]}`
}

export function fileNameFromDisposition(header) {
  if (!header) return null
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim())
    } catch (e) {
      // fall through to the plain filename
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1].trim() : null
}

/**
 * Download a file produced by an export endpoint through axios with the
 * token, then hand it to the browser as a normal file save.
 */
export async function downloadLaravelFile(path, { get = axios.get } = {}) {
  const res = await get(`${process.env.REACT_APP_LARAVEL_API_ENDPOINT}/download/${path}`, {
    responseType: 'blob',
    headers: authHeaders(),
  })
  const headers = res.headers || {}
  const name = fileNameFromDisposition(headers['content-disposition']) || fileNameOfDownloadPath(path)
  const url = window.URL.createObjectURL(res.data)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  return name
}
